import { parserCtx } from "@milkdown/core";
import { $command, $ctx, $prose } from "@milkdown/utils";
import { Plugin, PluginKey } from "@milkdown/prose/state";
import { Change, ChangeSet } from "@milkdown/prose/changeset";
import { Fragment, Slice } from "@milkdown/prose/model";
import { ReplaceStep } from "@milkdown/prose/transform";
//#region src/__internal__/with-meta.ts
function withMeta(plugin, meta) {
	Object.assign(plugin, { meta: {
		package: "@milkdown/plugin-diff",
		...meta
	} });
	return plugin;
}
//#endregion
//#region src/diff-compute.ts
var LCS_MAX_CHILDREN = 500;
/**
* Create a token encoder that encodes ALL non-default attrs for every node,
* but skips attrs listed in the `ignoreAttrs` map for a given node type.
*/
function createDiffEncoder(ignoreAttrs = {}) {
	const singleMarkCache = /* @__PURE__ */ new WeakMap();
	const markSetCache = /* @__PURE__ */ new WeakMap();
	function encodeMark(m) {
		let token = singleMarkCache.get(m);
		if (token != null) return token;
		const attrs = m.attrs;
		const keys = attrs ? Object.keys(attrs).filter((k) => attrs[k] != null).sort() : [];
		if (keys.length === 0) token = m.type.name;
		else {
			const encoded = {};
			for (const k of keys) encoded[k] = attrs[k];
			token = `${m.type.name}:${JSON.stringify(encoded)}`;
		}
		singleMarkCache.set(m, token);
		return token;
	}
	return {
		encodeCharacter: (char, marks) => {
			if (marks.length === 0) return char;
			let combined = markSetCache.get(marks);
			if (combined == null) {
				combined = marks.map(encodeMark).join(",");
				markSetCache.set(marks, combined);
			}
			return `${char}:${combined}`;
		},
		encodeNodeStart: (node) => {
			const attrs = node.attrs;
			if (attrs && Object.keys(attrs).length > 0) {
				const ignored = ignoreAttrs[node.type.name] ?? [];
				const relevantKeys = Object.keys(attrs).filter((key) => {
					if (ignored.includes(key)) return false;
					const defaultVal = node.type.spec.attrs?.[key]?.default;
					return attrs[key] !== defaultVal;
				});
				if (relevantKeys.length > 0) {
					const encoded = {};
					for (const key of relevantKeys.sort()) encoded[key] = attrs[key];
					return `${node.type.name}:${JSON.stringify(encoded)}`;
				}
			}
			return node.type.name;
		},
		encodeNodeEnd: (node) => {
			const schema = node.type.schema;
			const cache = schema.cached.changeSetIDs || (schema.cached.changeSetIDs = Object.create(null));
			let id = cache[node.type.name];
			if (id == null) cache[node.type.name] = id = Object.keys(schema.nodes).indexOf(node.type.name) + 1;
			return -id;
		},
		compareTokens: (a, b) => a === b
	};
}
function nodeSignature(node, encoder, cache) {
	const cached = cache.get(node);
	if (cached != null) return cached;
	const parts = [String(encoder.encodeNodeStart(node))];
	if (node.isText) {
		const text = node.text;
		for (let i = 0; i < text.length; i++) parts.push(":" + String(encoder.encodeCharacter(text.charCodeAt(i), node.marks)));
	} else node.content.forEach((child) => {
		parts.push("/" + nodeSignature(child, encoder, cache));
	});
	parts.push("\\" + String(encoder.encodeNodeEnd(node)));
	const sig = parts.join("");
	cache.set(node, sig);
	return sig;
}
function makeParentPair(node, contentStart, env) {
	const content = [];
	let offset = 0;
	node.content.forEach((child) => {
		content.push({
			node: child,
			offset,
			size: child.nodeSize,
			signature: nodeSignature(child, env.encoder, env.sigCache)
		});
		offset += child.nodeSize;
	});
	return {
		node,
		contentStart,
		content
	};
}
function lcsMatch(oldList, newList) {
	const n = oldList.length;
	const m = newList.length;
	const dp = Array.from({ length: n + 1 }, () => Array.from({ length: m + 1 }, () => 0));
	for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) if (oldList[i].signature === newList[j].signature) dp[i][j] = dp[i + 1][j + 1] + 1;
	else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
	const matches = [];
	let i = 0;
	let j = 0;
	while (i < n && j < m) if (oldList[i].signature === newList[j].signature) {
		matches.push([i, j]);
		i++;
		j++;
	} else if (dp[i + 1][j] >= dp[i][j + 1]) i++;
	else j++;
	return matches;
}
function canRecurse(oldNode, newNode) {
	if (oldNode.type !== newNode.type) return false;
	const type = oldNode.type;
	if (type.isTextblock) return false;
	if (type.isAtom) return false;
	if (type.spec.code === true) return false;
	if (!type.isBlock) return false;
	return true;
}
function translateChange(change, absA, absB) {
	return Change.fromJSON({
		fromA: change.fromA + absA,
		toA: change.toA + absA,
		fromB: change.fromB + absB,
		toB: change.toB + absB,
		deleted: change.deleted.map((s) => ({
			length: s.length,
			data: s.data
		})),
		inserted: change.inserted.map((s) => ({
			length: s.length,
			data: s.data
		}))
	});
}
function diffPairWithChangeSet(oldSide, newSide, env) {
	const wrapperOld = oldSide.parent.copy(Fragment.from(oldSide.node));
	const wrapperNew = newSide.parent.copy(Fragment.from(newSide.node));
	const step = new ReplaceStep(0, wrapperOld.content.size, new Slice(wrapperNew.content, 0, 0));
	return ChangeSet.create(wrapperOld, void 0, env.encoder).addSteps(wrapperNew, [step.getMap()], null).changes.map((c) => translateChange(c, oldSide.abs, newSide.abs));
}
function anchorOffset(pair, cursor) {
	const list = pair.content;
	if (cursor < list.length) return pair.contentStart + list[cursor].offset;
	if (cursor > 0) {
		const prev = list[cursor - 1];
		return pair.contentStart + prev.offset + prev.size;
	}
	return pair.contentStart + pair.node.content.size;
}
function pureDelete(child, absA, anchorB) {
	return Change.fromJSON({
		fromA: absA,
		toA: absA + child.size,
		fromB: anchorB,
		toB: anchorB,
		deleted: [{
			length: child.size,
			data: null
		}],
		inserted: []
	});
}
function pureInsert(child, anchorA, absB) {
	return Change.fromJSON({
		fromA: anchorA,
		toA: anchorA,
		fromB: absB,
		toB: absB + child.size,
		deleted: [],
		inserted: [{
			length: child.size,
			data: null
		}]
	});
}
function diffContainerContent(oldNode, newNode, oldContentStart, newContentStart, env) {
	const step = new ReplaceStep(0, oldNode.content.size, new Slice(newNode.content, 0, 0));
	return ChangeSet.create(oldNode, void 0, env.encoder).addSteps(newNode, [step.getMap()], null).changes.map((c) => translateChange(c, oldContentStart, newContentStart));
}
function diffChildrenLcs(oldNode, newNode, oldContentStart, newContentStart, env) {
	if (oldNode.childCount > LCS_MAX_CHILDREN || newNode.childCount > LCS_MAX_CHILDREN) return diffContainerContent(oldNode, newNode, oldContentStart, newContentStart, env);
	const oldPair = makeParentPair(oldNode, oldContentStart, env);
	const newPair = makeParentPair(newNode, newContentStart, env);
	const matches = lcsMatch(oldPair.content, newPair.content);
	const result = [];
	let i = 0;
	let j = 0;
	for (const [mi, mj] of matches) {
		processGap(oldPair, newPair, {
			oldStart: i,
			oldEnd: mi,
			newStart: j,
			newEnd: mj
		}, env, result);
		i = mi + 1;
		j = mj + 1;
	}
	processGap(oldPair, newPair, {
		oldStart: i,
		oldEnd: oldPair.content.length,
		newStart: j,
		newEnd: newPair.content.length
	}, env, result);
	return result;
}
function processGap(oldPair, newPair, window, env, out) {
	const oldList = oldPair.content;
	const newList = newPair.content;
	let oi = window.oldStart;
	let ni = window.newStart;
	while (oi < window.oldEnd || ni < window.newEnd) {
		const oldChild = oi < window.oldEnd ? oldList[oi] : null;
		const newChild = ni < window.newEnd ? newList[ni] : null;
		const sameType = oldChild && newChild && oldChild.node.type === newChild.node.type;
		if (oldChild && newChild && sameType) {
			out.push(...diffSameTypePair(oldPair, newPair, oldChild, newChild, env));
			oi++;
			ni++;
			continue;
		}
		if (oldChild && (!newChild || !sameType)) {
			const absA = oldPair.contentStart + oldChild.offset;
			out.push(pureDelete(oldChild, absA, anchorOffset(newPair, ni)));
			oi++;
			continue;
		}
		if (newChild) {
			const absB = newPair.contentStart + newChild.offset;
			out.push(pureInsert(newChild, anchorOffset(oldPair, oi), absB));
			ni++;
		}
	}
}
function diffSameTypePair(oldPair, newPair, oldChild, newChild, env) {
	const absA = oldPair.contentStart + oldChild.offset;
	const absB = newPair.contentStart + newChild.offset;
	if (!(canRecurse(oldChild.node, newChild.node) && attrsEqual(oldChild.node, newChild.node, env.encoder))) return diffPairWithChangeSet({
		node: oldChild.node,
		parent: oldPair.node,
		abs: absA
	}, {
		node: newChild.node,
		parent: newPair.node,
		abs: absB
	}, env);
	return diffChildrenLcs(oldChild.node, newChild.node, absA + 1, absB + 1, env);
}
function attrsEqual(a, b, encoder) {
	return encoder.encodeNodeStart(a) === encoder.encodeNodeStart(b);
}
function buildRangeSubtree(oldDoc, newDoc, from, to, encoder) {
	const $oldFrom = oldDoc.resolve(from);
	const $oldTo = oldDoc.resolve(to);
	const $newFrom = newDoc.resolve(from);
	const $newTo = newDoc.resolve(to);
	const sharedDepth = $oldFrom.sharedDepth(to);
	if ($newFrom.sharedDepth(to) !== sharedDepth) throw new RangeError(`computeDocDiff: range [${from}, ${to}) resolves to different sharedDepth in old and new docs`);
	for (let d = 0; d <= sharedDepth; d++) {
		if (encoder.encodeNodeStart($oldFrom.node(d)) !== encoder.encodeNodeStart($newFrom.node(d))) throw new RangeError(`computeDocDiff: ancestor at depth ${d} differs in type or non-ignored attrs along the path to the shared ancestor`);
		if ($oldFrom.start(d) !== $newFrom.start(d)) throw new RangeError(`computeDocDiff: ancestor at depth ${d} starts at different absolute positions in old and new docs (content before the range differs in size)`);
	}
	const sharedOld = $oldFrom.node(sharedDepth);
	if (sharedOld.isTextblock) throw new RangeError(`computeDocDiff: range [${from}, ${to}) lands inside a textblock; widen the range to a block boundary`);
	if ($oldFrom.depth !== sharedDepth || $oldTo.depth !== sharedDepth || $newFrom.depth !== sharedDepth || $newTo.depth !== sharedDepth) throw new RangeError(`computeDocDiff: range [${from}, ${to}) endpoints must be aligned to child boundaries of the shared ancestor`);
	const sharedNew = $newFrom.node(sharedDepth);
	const ancestorContentStart = $oldFrom.start(sharedDepth);
	const localFrom = from - ancestorContentStart;
	const localTo = to - ancestorContentStart;
	return {
		oldCut: sharedOld.copy(sharedOld.content.cut(localFrom, localTo)),
		newCut: sharedNew.copy(sharedNew.content.cut(localFrom, localTo)),
		cutAbsStart: from
	};
}
function computeDocDiff(oldDoc, newDoc, options) {
	const encoder = createDiffEncoder(options?.ignoreAttrs);
	const env = {
		encoder,
		sigCache: /* @__PURE__ */ new WeakMap()
	};
	const oldSize = oldDoc.content.size;
	const newSize = newDoc.content.size;
	const range = options?.range;
	if (range == null) return diffChildrenLcs(oldDoc, newDoc, 0, 0, env);
	const minSize = Math.min(oldSize, newSize);
	const from = Math.max(0, Math.min(range.from ?? 0, minSize));
	const to = Math.max(from, Math.min(range.to ?? minSize, minSize));
	if (from === to) return [];
	if (from === 0 && to === minSize && oldSize === newSize) return diffChildrenLcs(oldDoc, newDoc, 0, 0, env);
	const subtree = buildRangeSubtree(oldDoc, newDoc, from, to, encoder);
	return diffChildrenLcs(subtree.oldCut, subtree.newCut, subtree.cutAbsStart, subtree.cutAbsStart, env);
}
//#endregion
//#region src/diff-config.ts
var diffConfig = $ctx({ ignoreAttrs: { heading: ["id"] } }, "diffConfig");
withMeta(diffConfig, {
	displayName: "Ctx<diffConfig>",
	group: "Diff"
});
//#endregion
//#region src/diff-plugin.ts
var diffPluginKey = new PluginKey("MILKDOWN_DIFF");
function recomputeChanges(doc, state, options) {
	const changes = computeDocDiff(doc, state.newDoc, options);
	return {
		...state,
		changes
	};
}
function isChangeRejected(change, rejectedRanges) {
	return rejectedRanges.some((r) => change.fromB < r.toB && change.toB > r.fromB);
}
function getPendingChanges(state) {
	return state.changes.filter((c) => !isChangeRejected(c, state.rejectedRanges));
}
var diffPlugin = $prose((ctx) => {
	const config = ctx.get(diffConfig.key);
	return new Plugin({
		key: diffPluginKey,
		state: {
			init: () => null,
			apply(tr, value, _oldEditorState, newEditorState) {
				const action = tr.getMeta(diffPluginKey);
				if (!value) {
					if (action?.type === "start") {
						const changes = computeDocDiff(newEditorState.doc, action.newDoc, { ignoreAttrs: config.ignoreAttrs });
						return {
							newDoc: action.newDoc,
							changes,
							rejectedRanges: [],
							active: true
						};
					}
					return null;
				}
				let state = value;
				if (tr.docChanged && state.active) state = recomputeChanges(newEditorState.doc, state, { ignoreAttrs: config.ignoreAttrs });
				if (!action) return state;
				let result;
				switch (action.type) {
					case "start": {
						const changes = computeDocDiff(newEditorState.doc, action.newDoc, { ignoreAttrs: config.ignoreAttrs });
						return {
							newDoc: action.newDoc,
							changes,
							rejectedRanges: [],
							active: true
						};
					}
					case "accept":
					case "acceptRange":
						result = state;
						break;
					case "reject":
						result = {
							...state,
							rejectedRanges: [...state.rejectedRanges, {
								fromB: action.fromB,
								toB: action.toB
							}]
						};
						break;
					case "rejectRange":
						result = {
							...state,
							rejectedRanges: [...state.rejectedRanges, {
								fromB: action.range.fromB,
								toB: action.range.toB
							}]
						};
						break;
					case "acceptAll":
					case "clear": return null;
					default: return state;
				}
				if (result.active && getPendingChanges(result).length === 0) return null;
				return result;
			}
		},
		filterTransaction(tr, editorState) {
			if (!diffPluginKey.getState(editorState)?.active) return true;
			if (tr.getMeta(diffPluginKey)) return true;
			if (tr.docChanged) return false;
			return true;
		}
	});
});
withMeta(diffPlugin, {
	displayName: "Prose<diff>",
	group: "Diff"
});
//#endregion
//#region src/diff-commands.ts
/**
* Apply pending (non-rejected) changes from last to first.
* Reverse iteration is safe because changeset guarantees
* changes are ordered and non-overlapping.
*/
function applyPendingChanges(tr, diffState) {
	const pending = getPendingChanges(diffState);
	for (let i = pending.length - 1; i >= 0; i--) {
		const change = pending[i];
		const newContent = diffState.newDoc.slice(change.fromB, change.toB);
		tr = tr.replace(change.fromA, change.toA, newContent);
	}
	return tr;
}
var startDiffReviewCmd = $command("StartDiffReview", (ctx) => {
	return (modifiedMarkdown) => (state, dispatch) => {
		if (modifiedMarkdown == null) return false;
		const newDoc = ctx.get(parserCtx)(modifiedMarkdown);
		if (!newDoc) return false;
		if (dispatch) dispatch(state.tr.setMeta(diffPluginKey, {
			type: "start",
			newDoc
		}));
		return true;
	};
});
withMeta(startDiffReviewCmd, {
	displayName: "Command<startDiffReview>",
	group: "Diff"
});
var startDiffReviewFromDocCmd = $command("StartDiffReviewFromDoc", () => {
	return (newDoc) => (state, dispatch) => {
		if (!newDoc) return false;
		if (newDoc.type !== state.doc.type) return false;
		if (dispatch) dispatch(state.tr.setMeta(diffPluginKey, {
			type: "start",
			newDoc
		}));
		return true;
	};
});
withMeta(startDiffReviewFromDocCmd, {
	displayName: "Command<startDiffReviewFromDoc>",
	group: "Diff"
});
var acceptDiffChunkCmd = $command("AcceptDiffChunk", () => {
	return (changeIndex) => (state, dispatch) => {
		if (changeIndex == null) return false;
		const diffState = diffPluginKey.getState(state);
		if (!diffState) return false;
		const change = getPendingChanges(diffState)[changeIndex];
		if (!change) return false;
		if (dispatch) {
			const newContent = diffState.newDoc.slice(change.fromB, change.toB);
			let tr = state.tr.replace(change.fromA, change.toA, newContent);
			tr = tr.setMeta(diffPluginKey, {
				type: "accept",
				changeIndex
			});
			dispatch(tr);
		}
		return true;
	};
});
withMeta(acceptDiffChunkCmd, {
	displayName: "Command<acceptDiffChunk>",
	group: "Diff"
});
var rejectDiffChunkCmd = $command("RejectDiffChunk", () => {
	return (changeIndex) => (state, dispatch) => {
		if (changeIndex == null) return false;
		const diffState = diffPluginKey.getState(state);
		if (!diffState) return false;
		const change = getPendingChanges(diffState)[changeIndex];
		if (!change) return false;
		if (dispatch) dispatch(state.tr.setMeta(diffPluginKey, {
			type: "reject",
			fromB: change.fromB,
			toB: change.toB
		}));
		return true;
	};
});
withMeta(rejectDiffChunkCmd, {
	displayName: "Command<rejectDiffChunk>",
	group: "Diff"
});
var acceptDiffRangeCmd = $command("AcceptDiffRange", () => {
	return (range) => (state, dispatch) => {
		if (!range) return false;
		const diffState = diffPluginKey.getState(state);
		if (!diffState) return false;
		if (dispatch) {
			const newContent = diffState.newDoc.slice(range.fromB, range.toB);
			let tr = state.tr.replace(range.fromA, range.toA, newContent);
			tr = tr.setMeta(diffPluginKey, {
				type: "acceptRange",
				range
			});
			dispatch(tr);
		}
		return true;
	};
});
withMeta(acceptDiffRangeCmd, {
	displayName: "Command<acceptDiffRange>",
	group: "Diff"
});
var rejectDiffRangeCmd = $command("RejectDiffRange", () => {
	return (range) => (state, dispatch) => {
		if (!range) return false;
		if (!diffPluginKey.getState(state)) return false;
		if (dispatch) dispatch(state.tr.setMeta(diffPluginKey, {
			type: "rejectRange",
			range
		}));
		return true;
	};
});
withMeta(rejectDiffRangeCmd, {
	displayName: "Command<rejectDiffRange>",
	group: "Diff"
});
var acceptAllDiffsCmd = $command("AcceptAllDiffs", () => {
	return () => (state, dispatch) => {
		const diffState = diffPluginKey.getState(state);
		if (!diffState) return false;
		if (dispatch) dispatch((diffState.rejectedRanges.length === 0 ? state.tr.replaceWith(0, state.doc.content.size, diffState.newDoc.content) : applyPendingChanges(state.tr, diffState)).setMeta(diffPluginKey, { type: "acceptAll" }));
		return true;
	};
});
withMeta(acceptAllDiffsCmd, {
	displayName: "Command<acceptAllDiffs>",
	group: "Diff"
});
var clearDiffReviewCmd = $command("ClearDiffReview", () => {
	return () => (state, dispatch) => {
		if (!diffPluginKey.getState(state)) return false;
		if (dispatch) dispatch(state.tr.setMeta(diffPluginKey, { type: "clear" }));
		return true;
	};
});
withMeta(clearDiffReviewCmd, {
	displayName: "Command<clearDiffReview>",
	group: "Diff"
});
//#endregion
//#region src/index.ts
var diff = [
	diffConfig,
	diffPlugin,
	startDiffReviewCmd,
	startDiffReviewFromDocCmd,
	acceptDiffChunkCmd,
	acceptDiffRangeCmd,
	rejectDiffChunkCmd,
	rejectDiffRangeCmd,
	acceptAllDiffsCmd,
	clearDiffReviewCmd
];
//#endregion
export { acceptAllDiffsCmd, acceptDiffChunkCmd, acceptDiffRangeCmd, clearDiffReviewCmd, computeDocDiff, diff, diffConfig, diffPlugin, diffPluginKey, getPendingChanges, isChangeRejected, rejectDiffChunkCmd, rejectDiffRangeCmd, startDiffReviewCmd, startDiffReviewFromDocCmd };

//# sourceMappingURL=index.js.map